import axios from "axios";
import { useState } from "react";
import { BASE_URL, requestOptionsGET } from "../../../utils/Constants";
import { useAuth } from "../../../utils/context/AuthContext";

interface UseFileUploadOptions {
    patientId: string;
    folder: string | (() => string); // Can be a string or a function that returns a string
    onSuccess?: () => void;
    onError?: (error: string) => void;
}

export const useFileUpload = ({ patientId, folder, onSuccess, onError }: UseFileUploadOptions) => {
    const { auth } = useAuth();
    const [isUploading, setIsUploading] = useState(false);
    const [uploadedFile, setUploadedFile] = useState<File>();

    const onFileSelect = (file: File) => {
        setUploadedFile(file);
    };

    const onFileRemove = () => {
        setUploadedFile(undefined);
    };

    const getFolderName = (): string => {
        if (typeof folder === 'function') {
            return folder();
        }
        return folder;
    };

    const uploadFile = async () => {
        if (!auth.idToken || !uploadedFile) return;
        
        setIsUploading(true);
        const filename = uploadedFile.name;
        const folderName = getFolderName();
        
        try {
            const response = await fetch(
                `${BASE_URL}/api/health-plans/user/${patientId}/upload-url?filename=${filename}&folder=${folderName}`, 
                requestOptionsGET(auth.idToken)
            );
            
            if (response.ok) {
                const urlResponse = await response.json();
                const formData = new FormData();
                
                Object.entries(urlResponse.fields).forEach(([k, v]) => {
                    formData.append(k, v as unknown as string);
                });
                formData.append('file', uploadedFile);
                
                const s3response = await axios.post(urlResponse.url, formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    }
                });
                
                if (s3response.status === 204) {
                    console.log("File uploaded successfully");
                    setUploadedFile(undefined);
                    onSuccess?.();
                } else {
                    console.log("File not uploaded");
                    onError?.("Upload failed");
                }
            } else {
                const responseJson = await response.json();
                console.log(responseJson);
                onError?.("Upload failed");
            }
        } catch (error) {
            console.error("Upload error:", error);
            onError?.("Upload failed");
        } finally {
            setIsUploading(false);
        }
    };

    return {
        uploadedFile,
        isUploading,
        onFileSelect,
        onFileRemove,
        uploadFile
    };
}; 